import { useState, useEffect } from "react";
import { User, loadAllUsers, updateUserStatus, removeRegisteredUser, unlockUserAccount, deleteUserPermanently } from "@/utils/adminUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Unlock, RefreshCw, Edit, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface UsersListTabProps {
  users: User[];
  onUsersUpdate: (users: User[]) => void;
}

const UsersListTab = ({ users, onUsersUpdate }: UsersListTabProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [editingFeeUserId, setEditingFeeUserId] = useState<string | null>(null);
  const [editingFeeValue, setEditingFeeValue] = useState<string>("");
  const isMobile = useIsMobile();
  
  // Always load fresh user data directly from Supabase when the component mounts
  useEffect(() => {
    refreshUserList();
  }, []);

  const refreshUserList = async () => {
    console.log("Refreshing user list in UsersListTab");
    setIsLoading(true);
    
    try {
      // Load users from Supabase
      const allUsers = await loadAllUsers();
      onUsersUpdate(allUsers);
      
      toast({
        title: "User list refreshed",
        description: "Successfully loaded users from Supabase",
      });
    } catch (error) {
      console.error("Error loading users:", error);
      
      toast({
        title: "Error",
        description: "Failed to load users from Supabase",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateDeactivate = async (userId: string, newStatus: 'Active' | 'Inactive' | 'Pending' | 'Locked') => {
    try {
      const updatedUsers = await updateUserStatus(userId, newStatus);
      
      if (updatedUsers && updatedUsers.length > 0) {
        onUsersUpdate(updatedUsers);
        
        toast({
          title: `User ${newStatus === 'Active' ? 'Activated' : 'Deactivated'}`,
          description: `The user has been ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully.`,
          variant: newStatus === 'Active' ? 'default' : 'destructive',
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update user status. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while updating user status.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveUser = async (email: string) => {
    // Check if it's an admin email
    if (email === "stevenzicheng.sze@gmail.com" || email === "marcofilipi94@gmail.com") {
      toast({
        title: "Cannot Remove Admin",
        description: "Admin users cannot be removed from the system.",
        variant: "destructive",
      });
      return;
    }

    // Show confirmation dialog for permanent deletion
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETION WARNING ⚠️\n\nThis will PERMANENTLY DELETE the user "${email}" from:\n• Supabase Authentication\n• Database profiles (if exists)\n• All related data\n\nThis action CANNOT be undone!\n\nDo you want to proceed?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const result = await deleteUserPermanently(email);
      
      if (result) {
        // Refresh the user list
        await refreshUserList();
        
        toast({
          title: "User Successfully Deleted",
          description: `User "${email}" has been permanently removed from the system.`,
          variant: "default",
        });
      } else {
        toast({
          title: "Deletion Failed",
          description: "Failed to delete the user. This might be an orphaned auth record or the user doesn't exist.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting user permanently:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting the user. Check logs for details.",
        variant: "destructive",
      });
    }
  };

  const handleUnlockAccount = async (email: string) => {
    try {
      const success = await unlockUserAccount(email);
      
      if (success) {
        toast({
          title: "Account Unlocked",
          description: `Account with email ${email} has been unlocked successfully.`,
          variant: "default",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to unlock the account. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error unlocking account:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while unlocking the account.",
        variant: "destructive",
      });
    }
  };

  const handleEditApplicationFee = (user: User) => {
    setEditingFeeUserId(user.id);
    setEditingFeeValue(user.applicationFeePercent?.toString() || "20");
  };

  const handleSaveApplicationFee = async (userId: string) => {
    const feeValue = parseFloat(editingFeeValue);
    
    if (isNaN(feeValue) || feeValue < 0 || feeValue > 100) {
      toast({
        title: "Invalid Fee",
        description: "Application fee must be a number between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ application_fee_percent: feeValue })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      // Refresh the user list to show updated data
      await refreshUserList();
      
      setEditingFeeUserId(null);
      setEditingFeeValue("");
      
      toast({
        title: "Application Fee Updated",
        description: `Application fee set to ${feeValue}% successfully.`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error updating application fee:", error);
      toast({
        title: "Error",
        description: "Failed to update application fee. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancelEditApplicationFee = () => {
    setEditingFeeUserId(null);
    setEditingFeeValue("");
  };

  // Helper function to get the user's full name
  const getFullName = (user: User) => {
    // If name field contains a space, it likely has first and last name
    if (user.name && user.name.includes(' ')) {
      return user.name;
    }
    
    // Check for firstName and lastName
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    // Otherwise, just return the name field (or email as fallback)
    return user.name || user.email;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            Manage all user accounts
          </CardDescription>
        </div>
        <Button 
          onClick={refreshUserList}
          variant="outline"
          className="flex items-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Refresh Users
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        {users && users.length > 0 ? (
          isMobile ? (
            // Mobile Card Layout
            <div className="space-y-4">
              {users.map((user) => (
                <Card key={user.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* User Info */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm truncate">{getFullName(user)}</h3>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                          user.type === 'Admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : user.type === 'Mentor' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {user.type}
                        </span>
                      </div>

                      {/* Application Fee (for mentors) */}
                      {user.type === 'Mentor' && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">App Fee:</span>
                          {editingFeeUserId === user.id ? (
                            <div className="flex items-center space-x-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={editingFeeValue}
                                onChange={(e) => setEditingFeeValue(e.target.value)}
                                className="w-16 h-7 text-xs"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSaveApplicationFee(user.id)}
                                className="h-7 w-7 p-0"
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEditApplicationFee}
                                className="h-7 w-7 p-0"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs">
                                {user.applicationFeePercent ?? 20}%
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditApplicationFee(user)}
                                className="h-7 w-7 p-0"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Status */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-2">
                        {user.status === 'Active' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 text-xs"
                            onClick={() => handleActivateDeactivate(user.id, 'Inactive')}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 hover:bg-green-50 text-xs"
                            onClick={() => handleActivateDeactivate(user.id, 'Active')}
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-yellow-600 hover:bg-yellow-50 text-xs"
                          onClick={() => handleUnlockAccount(user.email)}
                        >
                          <Unlock className="h-3 w-3 mr-1" />
                          Unlock
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 text-xs col-span-2"
                          onClick={() => handleRemoveUser(user.email)}
                          title="Permanently delete user"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove User
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Desktop Table Layout
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Account Type</TableHead>
                  <TableHead>Application Fee (%)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{getFullName(user)}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.type === 'Admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : user.type === 'Mentor' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                      }`}>
                        {user.type}
                      </span>
                    </TableCell>
                    <TableCell>
                      {user.type === 'Mentor' ? (
                        editingFeeUserId === user.id ? (
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={editingFeeValue}
                              onChange={(e) => setEditingFeeValue(e.target.value)}
                              className="w-20 h-8"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSaveApplicationFee(user.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEditApplicationFee}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm">
                              {user.applicationFeePercent ?? 20}%
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditApplicationFee(user)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        )
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-gray-600 hover:bg-gray-50"
                        >
                          Edit
                        </Button>
                        {user.status === 'Active' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleActivateDeactivate(user.id, 'Inactive')}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => handleActivateDeactivate(user.id, 'Active')}
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-yellow-600 hover:bg-yellow-50"
                          onClick={() => handleUnlockAccount(user.email)}
                        >
                          <Unlock className="h-4 w-4 mr-1" />
                          Unlock
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleRemoveUser(user.email)}
                          title="Permanently delete user"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove User
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : (
          <div className="py-8 text-center text-gray-500">
            No users in the system yet.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsersListTab;
